import { CloseIcon } from "../../assets/Icons/CloseIcon";
import { ExpandIcon } from "../../assets/Icons/ExpandIcon";
import { ReduceIcon } from "../../assets/Icons/ReduceIcon";
import { InfoIcon } from "../../assets/Icons/InfoIcon";
import { AILogo } from "../../assets/Icons/SparksSolid";
import WarningTriangle from "../../assets/Icons/WarningTriangle";
import { WarningCircle } from "../../assets/Icons/WarningCircle";
import { CustomTooltip } from "../CommonUIComponents/CustomTooltip/CustomTooltip";
import { Chatbot } from "./Chatbot";
import "./Home.scss";
import OrderAssistantPanel from "../OrderAssistantPanel/OrderAssistantPanel";
import { InfoCircleIcon } from "../../assets/Icons/InfoCircleIcon";
import { CustomTooltipBottom } from "../CommonUIComponents/CustomTooltip/CustomTooltipBottom";
import SuperAgentChat from "../SuperAgentChat/SuperAgentChat";
import ChatInput from "../SuperAgentChat/ChatInput";
import { useState, useRef, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { NavDownArrow } from "../../assets/Icons/NavArrowDown";
import { OrdersService } from "../../api/services/orders.service";
import { abortCurrentRequest } from "../../api/services/ai.service";
import {
  HOME,
  WELCOME_TITLE,
  getWelcomeMessage,
  ARIA_LABELS,
  CHAT_ACTIONS,
} from "../../util/constants";
import { CustomTooltipTop } from "../CommonUIComponents/CustomTooltip/CustomTooltipTop";

// Main Home component with AI assistant and chat interface
function Home({
  showBackButton = false,
  onBackClick,
  onClose,
  onToggleFullScreen,
  isChatFullScreen = false,
  orderId,
  orderDetails: propOrderDetails,
  orderHome = false,
  onActionClick,
  onMessageSent,
  // onRefreshOrderDetails,
  onRefreshOrderDetails,
  dataReceivedFlowState,
  attachFilesMessages,
  incompleteDataMessage,
  onAILoadingChange,
  trailRefreshKey,
  notificationNavigationId,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentOrder } = useSelector((state) => state.orders);
  const user = useSelector((state) => state.user?.user);
  const selectedRole = useSelector((state) => state.user?.selectedRole);
  const userRoles = useSelector((state) => state.user?.userRoles);
  const rolesLoaded = useSelector((state) => state.user?.rolesLoaded);
  const userName = user?.name?.split(" ")[0] || "there";

  // Check if all non-admin roles are inactive
  const allRolesInactive =
    Array.isArray(userRoles) &&
    userRoles.length > 0 &&
    userRoles.every((role) => {
      const roleName =
        typeof role === "object" ? role.roleName || role.name : role;
      const isActive = typeof role === "object" ? role.status !== false : true;
      // Skip admin role when checking - only check non-admin roles
      if (roleName?.toLowerCase() === "admin") return true;
      return !isActive;
    });

  // Check if user is admin-only (has only admin role or all non-admin roles are inactive)
  const isAdminOnly =
    Array.isArray(userRoles) &&
    userRoles.length > 0 &&
    userRoles.every((role) => {
      const roleName =
        typeof role === "object" ? role.roleName || role.name : role;
      const isActive = typeof role === "object" ? role.status !== false : true;
      // User is admin-only if all roles are either admin or inactive
      return roleName?.toLowerCase() === "admin" || !isActive;
    });

  // Check if user has no roles at all (only after roles have been loaded)
  const hasNoRoles =
    rolesLoaded && (!Array.isArray(userRoles) || userRoles.length === 0);
  // Legacy (email-based orders):
  // const user = useSelector((state) => state.user?.user);
  // const userEmail = user?.username || user?.mail || "";
  const [orderDetails, setOrderDetails] = useState(propOrderDetails);
  const [isRecentOrdersOpen, setIsRecentOrdersOpen] = useState(false);
  const [recentOrders, setRecentOrders] = useState([]);
  const recentOrdersRef = useRef(null);

  // Update orderDetails when currentOrder changes in Redux store
  useEffect(() => {
    if (currentOrder) {
      setOrderDetails(currentOrder);
    }
  }, [currentOrder]);

  const showOrderPanel = Boolean(orderId);
  const [showPlanMyDay, setShowPlanMyDay] = useState(false);
  const [showHighPriorityOrders, setShowHighPriorityOrders] = useState(false);
  const [showOrdersInDataTriage, setShowOrdersInDataTriage] = useState(false);
  const [showNewlyAllocatedOrders, setShowNewlyAllocatedOrders] =
    useState(false);
  const [chatMode, setChatMode] = useState("planMyDay");
  const [startFresh, setStartFresh] = useState(false);
  const [isSuperAgentLoading, setIsSuperAgentLoading] = useState(false);
  const sendMessageRef = useRef(null);
  const newChatRequestRef = useRef(null);
  const location = useLocation();

  const currentHeaderOrder = Array.isArray(orderDetails)
    ? orderDetails[0]?.headerSection?.[0]
    : orderDetails;
  const currentAxonId = currentHeaderOrder?.axonId;

  // Check if order is active (controls chat and quick actions in order details page)
  const isActiveOrder = currentHeaderOrder?.activeOrder === true;

  const currentOrderDisplay = currentAxonId
    ? `AX-${currentAxonId}`
    : orderDetails?.axonId
      ? `AX-${orderDetails.axonId}`
      : "Loading...";

  const truncateClientEntity = (value, maxLength = 18) => {
    if (!value) return "";
    return value.length > maxLength ? `${value.slice(0, maxLength)}..` : value;
  };

  const formatAxonId = (value) => {
    if (!value) return "";
    const normalizedValue = String(value);
    return /^AX-/i.test(normalizedValue)
      ? normalizedValue.toUpperCase()
      : `AX-${normalizedValue}`;
  };

  // Handle navigation state to auto-expand chat with specific mode
  useEffect(() => {
    if (location.state?.chatMode) {
      const mode = location.state.chatMode;
      const initialMessage = location.state.initialMessage;
      setChatMode(mode);

      // Reset all states first
      setShowPlanMyDay(false);
      setShowHighPriorityOrders(false);
      setShowOrdersInDataTriage(false);
      setShowNewlyAllocatedOrders(false);

      // Set the specific mode to true
      switch (mode) {
        case "planMyDay":
          setShowPlanMyDay(true);
          break;
        case "highPriorityOrders":
          setShowHighPriorityOrders(true);
          break;
        case "ordersInDataTriage":
          setShowOrdersInDataTriage(true);
          break;
        case "newlyAllocatedOrders":
          setShowNewlyAllocatedOrders(true);
          break;
        default:
          break;
      }

      // If there's an initial message, send it after chat mode is set
      if (initialMessage) {
        setStartFresh(true);
        setTimeout(() => {
          if (sendMessageRef.current) {
            sendMessageRef.current(initialMessage);
          }
        }, 200);
      }

      // Clear the navigation state to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Reset state when navigating to home page without specific mode
  useEffect(() => {
    if (!location.state?.chatMode) {
      setShowPlanMyDay(false);
      setShowHighPriorityOrders(false);
      setShowOrdersInDataTriage(false);
      setShowNewlyAllocatedOrders(false);
      setChatMode("planMyDay");
    }
  }, [location.key]);

  const selectedRoleId = selectedRole?.roleId || "";

  // Loads a small, deduplicated list for recently opened orders dropdown.
  useEffect(() => {
    // Legacy (email-based orders):
    // if (!showOrderPanel || !userEmail) {
    //   return;
    // }
    if (!showOrderPanel || !selectedRoleId) {
      setRecentOrders([]);
      return;
    }

    let isCancelled = false;

    const fetchRecentOrders = async () => {
      try {
        const response = await OrdersService.getRecentlyViewedOrders({
          page: 1,
          pageSize: 100,
          roleId: selectedRoleId,
        });
        const responseData = response?.data || {};
        const recentOrdersFromApi = responseData?.recentOrders || [];

        const uniqueOrders = [];
        const seen = new Set();

        recentOrdersFromApi.forEach((order) => {
          const uniqueKey = order?.guid || order?.requestId;
          if (!uniqueKey || seen.has(uniqueKey)) {
            return;
          }
          seen.add(uniqueKey);
          uniqueOrders.push({
            ...order,
            // Keep existing UI shape expected by dropdown rendering.
            axonId: order?.axonId || order?.requestId,
          });
        });

        if (!isCancelled) {
          setRecentOrders(uniqueOrders.slice(0, 5));
        }
      } catch (error) {
        if (!isCancelled) {
          setRecentOrders([]);
        }
      }
    };

    fetchRecentOrders();

    return () => {
      isCancelled = true;
    };
    // Legacy dependency list: [showOrderPanel, userEmail, orderId]
  }, [showOrderPanel, orderId, selectedRoleId]);

  // Closes recent orders dropdown when clicking outside.
  useEffect(() => {
    if (!isRecentOrdersOpen) {
      return;
    }

    const handleOutsideClick = (event) => {
      if (
        recentOrdersRef.current &&
        !recentOrdersRef.current.contains(event.target)
      ) {
        setIsRecentOrdersOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isRecentOrdersOpen]);

  // Activates Plan My Day chat mode
  const handlePlanMyDayClick = () => {
    // console.log("[handlePlanMyDayClick] Button clicked");
    setShowPlanMyDay(true);
    setShowHighPriorityOrders(false);
    setShowOrdersInDataTriage(false);
    setShowNewlyAllocatedOrders(false);
    setChatMode("planMyDay");
    setStartFresh(true);

    // Send the button text as user message after SuperAgentChat mounts
    // Pass staticViewType to skip AI API and directly fetch orders
    setTimeout(() => {
      // console.log(
      //   "[handlePlanMyDayClick] Timeout fired, sendMessageRef.current:",
      //   !!sendMessageRef.current,
      // );
      if (sendMessageRef.current) {
        // console.log(
        //   "[handlePlanMyDayClick] Calling sendMessageRef with:",
        //   "Please plan my day",
        //   "plan_my_day",
        // );
        sendMessageRef.current("Please plan my day", "plan_my_day");
      }
    }, 100);
  };

  // Activates High Priority Orders chat mode
  const handleHighPriorityOrdersClick = () => {
    setShowHighPriorityOrders(true);
    setShowPlanMyDay(false);
    setShowOrdersInDataTriage(false);
    setShowNewlyAllocatedOrders(false);
    setChatMode("highPriorityOrders");
    setStartFresh(true);

    // Send the button text as user message after SuperAgentChat mounts
    setTimeout(() => {
      if (sendMessageRef.current) {
        sendMessageRef.current("High Priority Orders");
      }
    }, 100);
  };

  // Activates Orders In Data Triage chat mode
  const handleOrdersInDataTriageClick = () => {
    setShowOrdersInDataTriage(true);
    setShowPlanMyDay(false);
    setShowHighPriorityOrders(false);
    setShowNewlyAllocatedOrders(false);
    setChatMode("ordersInDataTriage");
    setStartFresh(true);

    // Send the button text as user message after SuperAgentChat mounts
    // Pass staticViewType to skip AI API and directly fetch orders
    setTimeout(() => {
      if (sendMessageRef.current) {
        sendMessageRef.current("Orders in Data Triage", "orders_data_triage");
      }
    }, 100);
  };

  // Activates Newly Allocated Orders chat mode
  const handleNewlyAllocatedOrdersClick = () => {
    setShowNewlyAllocatedOrders(true);
    setShowPlanMyDay(false);
    setShowHighPriorityOrders(false);
    setShowOrdersInDataTriage(false);
    setChatMode("newlyAllocatedOrders");
    setStartFresh(true);

    // Send the button text as user message after SuperAgentChat mounts
    // Pass staticViewType to skip AI API and directly fetch orders
    setTimeout(() => {
      if (sendMessageRef.current) {
        sendMessageRef.current(
          "Newly allocated orders",
          "newly_allocated_orders",
        );
      }
    }, 100);
  };

  // Sends message to SuperAgentChat
  const handleSendMessage = (text) => {
    if (sendMessageRef.current) {
      sendMessageRef.current(text);
    }
  };

  // Handles messages from Chatbot component
  const handleSendFromChatbot = (text, staticViewType) => {
    if (onMessageSent) {
      onMessageSent(text, staticViewType);
      return;
    }

    // Show plan my day mode
    setShowPlanMyDay(true);
    setShowHighPriorityOrders(false);
    setShowOrdersInDataTriage(false);
    setShowNewlyAllocatedOrders(false);
    setChatMode("planMyDay");
    setStartFresh(true);

    // Send the message after a brief delay to ensure SuperAgentChat is mounted
    setTimeout(() => {
      if (sendMessageRef.current) {
        // Pass staticViewType to skip AI API call when available
        sendMessageRef.current(text, staticViewType);
      }
    }, 100);
  };

  // Handles suggestion chip clicks
  const handleChipClick = (text, mode) => {
    if (newChatRequestRef.current) {
      newChatRequestRef.current(text, mode);
      setChatMode(mode);
    }
  };

  const handleRecentOrderSelect = (order) => {
    const targetOrderId = order?.guid || order?.orderId || order?.axonId;
    if (!targetOrderId) {
      return;
    }

    setIsRecentOrdersOpen(false);
    // Pass a unique recentOrderNavigationId to force fresh query API call when switching orders
    navigate(`/orders/${targetOrderId}`, {
      state: {
        orderDetails: order,
        recentOrderNavigationId: Date.now(),
      },
    });
  };

  return (
    <>
      <div
        className={
          showOrderPanel ? "home-container-with-orders" : "home-container"
        }
      >
        <div
          className={`home-header-row ${showBackButton ? "with-controls" : ""}`}
        >
          {showBackButton && (
            <div className="home-AI">
              <button
                className="home-back-button"
                aria-label={ARIA_LABELS.GO_BACK}
              >
                <AILogo width="15" height="15" />
              </button>
              <p>{HOME.ASSISTANT}</p>
            </div>
          )}
          {/* Super Agent header for non-order pages */}
          {!showOrderPanel && !isAdminOnly && !hasNoRoles && (
            <div className="home-center-text">
              {HOME.SUPER_AGENT}
              <div className="userid-display-icon">
                <CustomTooltipBottom
                  trigger={<InfoCircleIcon width="15" height="15" />}
                  boldText={HOME.SUPER_AGENT}
                  normalText={HOME.SUPER_AGENT_TOOLTIP}
                />
              </div>
            </div>
          )}
          {/* No roles error banner */}
          {!showOrderPanel && hasNoRoles && (
            <div className="no-roles-error-header">
              <WarningCircle width={20} height={20} color="#EA011D" />
              <span>
                You do not currently have any roles assigned in this
                application. Please contact the administrator to request access.
              </span>
            </div>
          )}
          {/* Admin-only warning in header position */}
          {!showOrderPanel && isAdminOnly && !hasNoRoles && (
            <div className="admin-only-warning-header">
              <WarningTriangle width={20} height={20} color="#FF9831" />
              <span>
                No roles assigned. Please assign yourself a role to proceed.
              </span>
            </div>
          )}

          {showOrderPanel && (
            <div className="userid-display" ref={recentOrdersRef}>
              <div className="userid-display-icon">
                <CustomTooltipBottom
                  trigger={<InfoCircleIcon width="16" height="16" />}
                  boldText={HOME.ORDER_MODE}
                  normalText={HOME.ORDER_MODE_TOOLTIP}
                />
              </div>
              <button
                className="userid-display-trigger"
                onClick={() => setIsRecentOrdersOpen((prev) => !prev)}
                aria-label={ARIA_LABELS.EXPAND}
                type="button"
              >
                <div className="userid-display-text">{currentOrderDisplay}</div>
                <NavDownArrow width={16} height={16} color="#ffffff" />
              </button>
              {isRecentOrdersOpen && recentOrders.length > 0 && (
                <div className="recent-orders-dropdown">
                  <div className="recent-orders-title">
                    Recently open orders
                  </div>
                  <div className="recent-orders-list">
                    {recentOrders.map((order) => {
                      const rowId = order?.guid || order?.axonId;
                      const isSelected =
                        (order?.guid && order?.guid === orderId) ||
                        (order?.axonId && order?.axonId === currentAxonId);

                      return (
                        <button
                          key={rowId}
                          type="button"
                          className={`recent-order-item ${isSelected ? "active" : ""}`}
                          onClick={() => handleRecentOrderSelect(order)}
                        >
                          <span className="recent-order-item-text">
                            {/* {order?.axonId || order?.requestId} |{" "} */}
                            {formatAxonId(
                              order?.axonId || order?.requestId,
                            )} | {truncateClientEntity(order?.clientEntity)}
                          </span>
                          {isSelected && (
                            <span className="recent-order-check">✓</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {showBackButton && (
            <div className="home-close-expand">
              <button
                className="home-close-button"
                onClick={onToggleFullScreen}
                aria-label={
                  isChatFullScreen ? ARIA_LABELS.REDUCE : ARIA_LABELS.EXPAND
                }
              >
                {isChatFullScreen ? (
                  <ReduceIcon width="20" height="20" color="#ffffff" />
                ) : (
                  <ExpandIcon width="20" height="20" color="#ffffff" />
                )}
              </button>
              <button
                className="home-close-button"
                onClick={onClose}
                aria-label={ARIA_LABELS.CLOSE}
              >
                <CloseIcon width="20" height="20" color="#ffffff" />
              </button>
            </div>
          )}
        </div>

        {!showOrderPanel && (
          <div
            className={
              orderHome ? "order-home-welcome-box" : "home-welcome-box"
            }
          >
            {!showPlanMyDay &&
            !showHighPriorityOrders &&
            !showOrdersInDataTriage &&
            !showNewlyAllocatedOrders ? (
              <>
                <div className="home-welcome-image">
                  <img
                    src={require("../../assets/Images/Home/homeBigLogo.png")}
                    alt={HOME.WELCOME_ALT}
                  />
                </div>
                <div className="home-welcome-title">{WELCOME_TITLE}</div>
                <div className="home-welcome-message">
                  {getWelcomeMessage(
                    userName,
                    selectedRole?.roleName || selectedRole?.name,
                  )}
                </div>
                {/* Only show chatbot/no-access states after roles have been loaded */}
                {rolesLoaded &&
                  (!allRolesInactive || isAdminOnly || hasNoRoles) && (
                    <div
                      className={
                        isAdminOnly || hasNoRoles
                          ? "chatbot-disabled-wrapper"
                          : ""
                      }
                    >
                      <Chatbot
                        onPlanMyDay={handlePlanMyDayClick}
                        onHighPriorityOrders={handleHighPriorityOrdersClick}
                        onOrdersInDataTriage={handleOrdersInDataTriageClick}
                        onNewlyAllocatedOrders={handleNewlyAllocatedOrdersClick}
                        onSendToSuperAgent={handleSendFromChatbot}
                        isDisabled={isAdminOnly || hasNoRoles}
                        orderHome={orderHome}
                      />
                    </div>
                  )}
                {rolesLoaded &&
                  allRolesInactive &&
                  !isAdminOnly &&
                  !hasNoRoles && (
                    <div className="all-roles-inactive-message">
                      All your roles are currently inactive. Please contact your
                      administrator.
                    </div>
                  )}
              </>
            ) : (
              <div className="super-agent-container">
                <SuperAgentChat
                  onSendMessage={sendMessageRef}
                  onNewChatRequest={newChatRequestRef}
                  mode={chatMode}
                  startFresh={startFresh}
                  onLoadingChange={setIsSuperAgentLoading}
                />
                <ChatInput
                  onSend={handleSendMessage}
                  onChipClick={handleChipClick}
                  disabled={isSuperAgentLoading}
                  isLoading={isSuperAgentLoading}
                  onStop={() => {
                    abortCurrentRequest();
                    setIsSuperAgentLoading(false);
                  }}
                />
                {/* Disclaimer message */}
                <div className="super-agent-disclaimer">
                  <span>
                    {CHAT_ACTIONS.DISCLAIMER}
                    <CustomTooltipTop
                      trigger={
                        <InfoCircleIcon
                          width="14.33"
                          height="14.33"
                          color="#85B9FD"
                        />
                      }
                      normalText={CHAT_ACTIONS.DISCLAIMER_TOOLTIP}
                    />
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {showOrderPanel && (
          <OrderAssistantPanel
            orderId={orderId}
            orderDetails={orderDetails}
            onActionClick={onActionClick}
            onRefreshOrderDetails={onRefreshOrderDetails}
            dataReceivedFlowState={dataReceivedFlowState}
            attachFilesMessages={attachFilesMessages}
            incompleteDataMessage={incompleteDataMessage}
            isActiveOrder={isActiveOrder}
            onAILoadingChange={onAILoadingChange}
            trailRefreshKey={trailRefreshKey}
            notificationNavigationId={notificationNavigationId}
          />
        )}
      </div>
    </>
  );
}

export default Home;
